import { useMemo } from 'react'

import { Alert, Badge, Group, Stack, Switch, Text, TextInput, Title } from '@mantine/core'
import { IconAlertTriangle, IconInfoSquare, IconLayout } from '@tabler/icons-react'
import { useGetOne, useTranslate } from 'react-admin'
import {
  selectEndscreenConfig,
  selectInfocardsConfig,
  useVideoPromotionStore,
} from '../../../stores/useVideoPromotionStore'
import { YoutubeChannel } from '../../../types/YoutubeChannel'
import { PromotionConfigRecap } from '../PromotionConfigRecap'

type NewPromotionConfirmationProps = {
  name: string
  onNameChange: (value: string) => void
  overwriteExistingEndscreen: boolean
  onOverwriteExistingEndscreenChange: (value: boolean) => void
  overwriteExistingInfocards: boolean
  onOverwriteExistingInfocardsChange: (value: boolean) => void
}

export const NewPromotionConfirmation = ({
  name,
  onNameChange,
  overwriteExistingEndscreen,
  onOverwriteExistingEndscreenChange,
  overwriteExistingInfocards,
  onOverwriteExistingInfocardsChange,
}: NewPromotionConfirmationProps) => {
  const t = useTranslate()
  const videoPromotionStore = useVideoPromotionStore()

  const youtubeChannelQuery = useGetOne<YoutubeChannel>(
    'youtubeChannels',
    {
      id: videoPromotionStore.youtubeChannelId ?? '',
    },
    {
      enabled: !!videoPromotionStore.youtubeChannelId,
    },
  )

  const videosWithEndscreen = useMemo(
    () => videoPromotionStore.selectedVideos.filter(v => v.hasEndscreen).length,
    [videoPromotionStore.selectedVideos],
  )
  const videosWithInfocards = useMemo(
    () => videoPromotionStore.selectedVideos.filter(v => v.hasInfocards).length,
    [videoPromotionStore.selectedVideos],
  )

  const endScreenConfig = useMemo(
    () => selectEndscreenConfig(videoPromotionStore),
    [videoPromotionStore],
  )
  const infocardsConfig = useMemo(
    () => selectInfocardsConfig(videoPromotionStore),
    [videoPromotionStore],
  )

  return (
    <Stack maw={720} mt="md" gap="lg">
      <Stack gap="xs">
        <Title order={5}>{t('pages.videoPromotion.confirmation.title')}</Title>
        <Group justify="space-between">
          <Title order={6} fw={400} c="dimmed">
            {t('pages.videoPromotion.confirmation.appliedElements')}
          </Title>
          <Group gap="xs">
            {!!endScreenConfig && (
              <Badge leftSection={<IconLayout size={12} />} variant="light" color="blue">
                {t('pages.videoPromotion.endScreen._title')}
              </Badge>
            )}
            {!!infocardsConfig && (
              <Badge leftSection={<IconInfoSquare size={12} />} variant="light" color="violet">
                {t('pages.videoPromotion.infoCards._title')}
              </Badge>
            )}
          </Group>
        </Group>
        <Group justify="space-between">
          <Title order={6} fw={400} c="dimmed">
            {t('_generics.youtubeChannel')}
          </Title>
          <Text>{youtubeChannelQuery.data?.name ?? videoPromotionStore.youtubeChannelId}</Text>
        </Group>
        <Group justify="space-between">
          <Title order={6} fw={400} c="dimmed">
            {t('pages.videoPromotion.confirmation.videos')}
          </Title>
          <Text>{videoPromotionStore.youtubeVideoIds.length}</Text>
        </Group>
        {!!endScreenConfig && (
          <Group justify="space-between">
            <Title order={6} fw={400} c="dimmed">
              {t('pages.videoPromotion.endScreen._title')}
            </Title>
            <Text>
              {endScreenConfig.mode === 'copyFromVideo'
                ? t('pages.videoPromotion.confirmation.copyFromVideo', {
                    videoId: endScreenConfig.sourceVideoId,
                  })
                : t('pages.videoPromotion.confirmation.customEndscreen', {
                    layoutId: videoPromotionStore.endScreenLayoutId,
                  })}
            </Text>
          </Group>
        )}
        {!!infocardsConfig && (
          <Group justify="space-between">
            <Title order={6} fw={400} c="dimmed">
              {t('pages.videoPromotion.infoCards._title')}
            </Title>
            <Text>
              {infocardsConfig.mode === 'copyFromVideo'
                ? t('pages.videoPromotion.confirmation.copyFromVideo', {
                    videoId: infocardsConfig.sourceVideoId,
                  })
                : t('pages.videoPromotion.confirmation.customInfocards', {
                    smart_count: infocardsConfig.cards.length,
                  })}
            </Text>
          </Group>
        )}
      </Stack>
      {!!videoPromotionStore.youtubeChannelId && (
        <PromotionConfigRecap
          youtubeChannelId={videoPromotionStore.youtubeChannelId}
          endscreen={endScreenConfig}
          infocards={infocardsConfig}
        />
      )}
      {!!endScreenConfig && videosWithEndscreen > 0 && (
        <Alert
          color="orange"
          variant="light"
          icon={<IconAlertTriangle size={20} />}
          title={t('pages.videoPromotion.confirmation.videosWithEndscreen', {
            smart_count: videosWithEndscreen,
          })}
        >
          {t(
            overwriteExistingEndscreen
              ? 'pages.videoPromotion.confirmation.willOverwrite'
              : 'pages.videoPromotion.confirmation.willSkip',
          )}
        </Alert>
      )}
      {!!infocardsConfig && videosWithInfocards > 0 && (
        <Alert
          color="orange"
          variant="light"
          icon={<IconAlertTriangle size={20} />}
          title={t('pages.videoPromotion.confirmation.videosWithInfocards', {
            smart_count: videosWithInfocards,
          })}
        >
          {t(
            overwriteExistingInfocards
              ? 'pages.videoPromotion.confirmation.willOverwrite'
              : 'pages.videoPromotion.confirmation.willSkip',
          )}
        </Alert>
      )}
      <TextInput
        label={t('pages.videoPromotion.confirmation.operationName')}
        value={name}
        onChange={event => onNameChange(event.currentTarget.value)}
      />
      <Switch
        label={t('pages.videoPromotion.confirmation.overwriteExistingEndscreen')}
        checked={overwriteExistingEndscreen}
        onChange={event => onOverwriteExistingEndscreenChange(event.currentTarget.checked)}
      />
      <Switch
        label={t('pages.videoPromotion.confirmation.overwriteExistingInfocards')}
        checked={overwriteExistingInfocards}
        onChange={event => onOverwriteExistingInfocardsChange(event.currentTarget.checked)}
      />
    </Stack>
  )
}
